import { PetMessage } from '.';
import * as PetChatbot from '.';
import { MutableState } from '../State';
import { Feature } from '../analytics';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import type {
  AsyncActionAPI,
  AnalyticsAPI,
} from 'src/replicant/config/actions/createActions';
import {
  chatbotMessageTemplates,
  generateReplicantChatbotPayload,
} from '../chatbot/messageTemplates';
import {
  getAnalyticsAttributeUserProperties,
  getAnalyticsUserProperties,
  getEquippedPetSpecies,
  getPetName,
} from '../pet/pet.getters';
import { getRandomItemFromArray, getRandomItemsFromArr } from '../utils/random';
import { substituteTokens } from './petChatbot.scheduledActions';
import { getPlayerProfile } from '../profile/profile.getters';
import { trackIcebreakerMessageSuccess } from './petChatbot.analytics';
import { getLocaleConfig } from '../creatives/text';
import { getLineRedirectURL } from '../utils/payload';

const fallbackUserName = {
  en: 'friend',
  ja: 'きみ',
};

/**
 * We required this type of API for KV storage
 */
type KVStorageAPI = {
  kvStore: AsyncActionAPI['kvStore'];
  reportError: AsyncActionAPI['reportError'];
  date: AsyncActionAPI['date'];
};

/**
 * Options for greetings pet chatbot response
 */
export type PetGreetingsResponseOptions = {
  type: 'morning' | 'lunch' | 'evening';
  channel: 'push' | 'oa';
  random: () => number;
};

/**
 * Should have async getters getShortLink API
 */
export type AsyncGettersAPI = {
  asyncGetters: {
    getShortLink: AsyncActionAPI['asyncGetters']['getShortLink'];
  };
};

/**
 * Options for invite greetings pet chatbot response
 */
export type PetInviteGreetingsResponseOptions = {
  type: 'morning' | 'lunch' | 'evening';
  random: () => number;
};

/**
 * Generate a default greeting response for specific time of the day and species.
 * It does not require any user input.
 */
export function petDefaultGreetingsResponse(
  state: MutableState,
  opts: PetGreetingsResponseOptions,
  api: AnalyticsAPI,
  now: number,
) {
  const species = getEquippedPetSpecies(state) ?? 'ball';
  const locale = state.settings.locale === 'en' ? 'en' : 'ja';
  const profile = getPlayerProfile(
    state,
    state.publicProfile?.name,
    undefined,
    fallbackUserName[locale],
    undefined,
  );

  // Get list of greetings  messages for the species
  const speciesMessages: PetMessage[] =
    PetChatbot.ruleset.petSpeciesMessages[species];

  // Choose random message for the time of the day
  const validMessages = speciesMessages.filter(
    ({ time }) => time === opts.type,
  );
  const messageSample =
    validMessages[Math.floor(opts.random() * validMessages.length)];

  // 1 - normal message, 2 - pet ai chatbot message
  const messageProp = `text_1_${locale === 'ja' ? 'jp' : 'en'}` as const;
  const messageText = messageSample[messageProp] as string;

  // Check if message exist
  if (!messageText) throw new Error('Message text is missing');

  // Replace tokens in the message
  let message = substituteTokens(messageText, {
    pet_name: getPetName(state),
    player_name: profile.name,
  });

  // Replace newlines with spaces for push notifications
  if (opts.channel === 'push') {
    message = message.replace(/\n\n/g, ' ');
  }

  // Get the user properties for analytics
  const payload = generateReplicantChatbotPayload({
    feature: Feature.GREETING._,
    $subFeature: opts.type,
  });

  // Combine payload and analytics user properties
  const payloadAndAnalytics = {
    payload,
    analyticsUserProperties: {
      ...getAnalyticsUserProperties(state, now),
      ...getAnalyticsAttributeUserProperties(state, now),
    },
  };

  // Analytics event
  trackIcebreakerMessageSuccess(state, Feature.GREETING._, payload, now, api);

  // Our default greeting message
  return chatbotMessageTemplates.greeting({
    args: {
      species,
      petName: getPetName(state),
      message,
      locale,
    },
    ...payloadAndAnalytics,
  });
}

/**
 * Generate a default greeting response for specific time of the day and species.
 * It does require user input, that will be handled by the ai chatbot.
 */
export async function petNewGreetingsResponse(
  state: MutableState,
  opts: PetGreetingsResponseOptions,
  api: KVStorageAPI & AnalyticsAPI,
  now: number,
) {
  const species = getEquippedPetSpecies(state) ?? 'ball';
  const locale = state.settings.locale === 'en' ? 'en' : 'jp';
  const profile = getPlayerProfile(
    state,
    state.publicProfile?.name,
    undefined,
    fallbackUserName[locale === 'en' ? 'en' : 'ja'],
    undefined,
  );

  // Get list of greetings  messages for the species
  const speciesMessages: PetMessage[] =
    PetChatbot.ruleset.petSpeciesMessages[species];

  // Choose random message for the time of the day
  const validMessages = speciesMessages.filter(
    ({ time }) => time === opts.type,
  );
  const messageSample =
    validMessages[Math.floor(opts.random() * validMessages.length)];

  // 1 - normal message, 2 - pet ai chatbot message
  const messageProp = `text_2_${locale}` as keyof typeof messageSample;
  const messageText = messageSample[messageProp] as string;

  // Check if message exist
  if (!messageText) throw new Error('Message text is missing');

  // Replace tokens in the message
  let message = substituteTokens(messageText, {
    pet_name: getPetName(state),
    player_name: profile.name,
  });

  // Replace newlines with spaces for push notifications
  if (opts.channel === 'push') {
    message = message.replace(/\n\n/g, ' ');
  }

  // Get the user properties for analytics
  const payload = generateReplicantChatbotPayload({
    feature: Feature.GREETING._,
    $subFeature: opts.type,
  });

  // Combine payload and analytics user properties
  const payloadAndAnalytics = {
    payload,
    analyticsUserProperties: {
      ...getAnalyticsUserProperties(state, now),
      ...getAnalyticsAttributeUserProperties(state, now),
    },
  };

  // Analytics event
  trackIcebreakerMessageSuccess(state, Feature.GREETING._, payload, now, api);

  // Our default greeting message or AI greeting message, no reply buttons
  return chatbotMessageTemplates.petChat({
    args: {
      species,
      petName: getPetName(state),
      message,
    },
    ...payloadAndAnalytics,
  });
}

/**
 * Generate a default greeting response for specific time of the day and species.
 * It does require user input, that will be handled by the ai chatbot.
 * It give user options to answer with line flex buttons.
 */
export async function petNewGreetingsFlexReplyResponse(
  state: MutableState,
  opts: PetGreetingsResponseOptions,
  api: KVStorageAPI & AnalyticsAPI,
  now: number,
) {
  const species = getEquippedPetSpecies(state) ?? 'ball';
  const locale = state.settings.locale === 'en' ? 'en' : 'jp';
  const profile = getPlayerProfile(
    state,
    state.publicProfile?.name,
    undefined,
    fallbackUserName[locale === 'en' ? 'en' : 'ja'],
    undefined,
  );

  // Get list of greetings  messages for the species
  const speciesMessages: PetMessage[] =
    PetChatbot.ruleset.petSpeciesMessages[species];

  // Choose random message for the time of the day
  const validMessages = speciesMessages.filter(
    ({ time }) => time === opts.type,
  );
  const messageSample =
    validMessages[Math.floor(opts.random() * validMessages.length)];

  // 1 - normal message, 2 - pet ai chatbot message
  const messageKey = `text_2_${locale}` as keyof typeof messageSample;
  const messageText = messageSample[messageKey] as string;
  const answerOptionsKey = `answers_2_${locale}` as keyof typeof messageSample;
  const answerOptionsAll = messageSample[answerOptionsKey] as
    | string[]
    | undefined;

  // We should select 3 random answers
  const answerOptions = getRandomItemsFromArr(
    answerOptionsAll ?? [],
    3,
    opts.random,
  );

  // Check if message exist
  if (!messageText) throw new Error('Message text is missing');

  // Replace tokens in the message
  let message = substituteTokens(messageText, {
    pet_name: getPetName(state),
    player_name: profile.name,
  });

  // Replace newlines with spaces for push notifications
  if (opts.channel === 'push') {
    message = message.replace(/\n\n/g, ' ');
  }

  // Get the user properties for analytics
  const payload = generateReplicantChatbotPayload({
    feature: Feature.GREETING._,
    $subFeature: opts.type,
  });

  // Analytics event
  trackIcebreakerMessageSuccess(state, Feature.GREETING._, payload, now, api);

  // Flex message with reply options
  return getChatbotFlexMessage({
    template: 'reply',
    payload,
    data: {
      species,
      petName: getPetName(state),
      message: messageText,
      select: answerOptions ?? [],
    },
  });
}

/**
 * Get one of predefined messages that ask user about sharing the game with friends
 * There are two options: share and do not share
 * In case of sharing, we generate a short link to the game and add it to the message. Line picker will be used for sharing.
 * If user does not want to share, we will just send a message, that can be used by AI to continue the conversation.
 */
export async function inviteGreetingResponse(
  state: MutableState,
  opts: PetInviteGreetingsResponseOptions,
  api: KVStorageAPI & AnalyticsAPI & AsyncGettersAPI,
  now: number,
) {
  const locale = state.settings.locale === 'en' ? 'en' : 'ja';

  // Get possible greeting messages
  const messages = getLocaleConfig(locale).inviteGreeting.messages[opts.type];
  const shareMessages = getLocaleConfig(locale).inviteGreeting.share;
  const buttons = getLocaleConfig(locale).inviteGreeting.buttons;
  const message = getRandomItemFromArray(messages, opts.random);
  const shareMessage = getRandomItemFromArray(shareMessages, opts.random);

  // Check if message exist
  if (!message) throw new Error('Message text is missing');
  if (!shareMessage) throw new Error('Share message text is missing');
  if (!buttons) throw new Error('Buttons text is missing');

  // Get the user properties for analytics
  const payload = generateReplicantChatbotPayload({
    $channel: 'INVITE',
    feature: Feature.GREETING.BOT_INVITE,
    $subFeature: opts.type,
    $creativeTextID: message.id,
    $creativeCTA: shareMessage.id,
  });

  // Create short link over playco.click (https://github.com/play-co/url-shortener)
  const { shortLink } = await api.asyncGetters.getShortLink({
    url: getLineRedirectURL(payload),
  });

  // Analytics event
  trackIcebreakerMessageSuccess(
    state,
    Feature.GREETING.BOT_INVITE,
    payload,
    now,
    api,
  );

  // Flex message with reply options
  return getChatbotFlexMessage({
    template: 'share',
    payload,
    data: {
      species: getEquippedPetSpecies(state) ?? 'ball',
      petName: getPetName(state),
      message: message.text,
      buttons,
      share: `${shareMessage.text}\n\n${shortLink}`,
    },
  });
}

/**
 * Generate an image greeting response for specific time of the day and species.
 * It does not require any user input.
 */
export function petImageGreetingsResponse(
  state: MutableState,
  opts: PetGreetingsResponseOptions,
  api: AnalyticsAPI,
  now: number,
) {
  const species = getEquippedPetSpecies(state) ?? 'ball';
  const locale = state.settings.locale === 'en' ? 'en' : 'ja';
  const profile = getPlayerProfile(
    state,
    state.publicProfile?.name,
    undefined,
    fallbackUserName[locale],
    undefined,
  );

  // Get list of greetings  messages for the species
  const speciesMessages: PetMessage[] =
    PetChatbot.ruleset.petSpeciesMessages[species];

  // Choose random message for the time of the day
  const validMessages = speciesMessages.filter(
    ({ time }) => time === opts.type,
  );
  const messageSample =
    validMessages[Math.floor(opts.random() * validMessages.length)];

  // 1 - normal message, 2 - pet ai chatbot message
  const messageProp = `text_1_${locale === 'ja' ? 'jp' : 'en'}` as const;
  const messageText = messageSample[messageProp] as string;

  // Check if message exist
  if (!messageText) throw new Error('Message text is missing');

  // Replace tokens in the message
  let message = substituteTokens(messageText, {
    pet_name: getPetName(state),
    player_name: profile.name,
  });

  // Replace double newlines with singles to fit in the image
  message = message.replace(/\n\n/g, '\n');

  // Replace newlines with spaces for push notifications
  if (opts.channel === 'push') {
    message = message.replace(/\n/g, ' ');
  }

  // Get the user properties for analytics
  const payload = generateReplicantChatbotPayload({
    feature: Feature.GREETING._,
    $subFeature: Feature.GREETING.HAPPY_PETS,
  });

  // Combine payload and analytics user properties
  const payloadAndAnalytics = {
    payload,
    analyticsUserProperties: {
      ...getAnalyticsUserProperties(state, now),
      ...getAnalyticsAttributeUserProperties(state, now),
    },
  };

  // Analytics event
  trackIcebreakerMessageSuccess(state, Feature.GREETING._, payload, now, api);

  // Our default greeting message
  return chatbotMessageTemplates.greetingImage({
    args: {
      species,
      petName: getPetName(state),
      message,
      locale,
      period: opts.type,
    },
    ...payloadAndAnalytics,
  });
}
